/**
 * TalentLens AI - WebSocket Client
 * Real-time updates for CV processing status
 */

type MessageHandler = (data: any) => void;
type ConnectionHandler = () => void;

interface WebSocketMessage {
  type: 'processing_started' | 'processing_progress' | 'processing_completed' | 'processing_failed' | 'connected' | 'subscribed' | 'pong';
  cvId?: string;
  cv_id?: string;  // Backend sends snake_case
  status?: string;
  progress?: number;
  progress_percent?: number;  // Backend sends snake_case
  error?: string;
  parsedData?: any;
}

class WebSocketClient {
  private ws: WebSocket | null = null;
  private url: string;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private messageHandlers: Map<string, Set<MessageHandler>> = new Map();
  private onConnectHandlers: Set<ConnectionHandler> = new Set();
  private onDisconnectHandlers: Set<ConnectionHandler> = new Set();

  constructor(url?: string) {
    // Get WebSocket URL from environment or use default
    const envUrl = (import.meta as any).env?.VITE_WS_URL;
    this.url = url || envUrl || 'ws://localhost:3000/ws';
  }

  connect(): void {
    if (this.ws?.readyState === WebSocket.OPEN) return;

    try {
      this.ws = new WebSocket(this.url);

      this.ws.onopen = () => {
        console.log('WebSocket connected');
        this.reconnectAttempts = 0;
        this.onConnectHandlers.forEach(handler => handler());
      };

      this.ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          this.handleMessage(message);
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };

      this.ws.onclose = () => {
        console.log('WebSocket disconnected');
        this.onDisconnectHandlers.forEach(handler => handler());
        this.attemptReconnect();
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
      this.attemptReconnect();
    }
  }

  private attemptReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('Max reconnect attempts reached');
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);

    console.log(`Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);
    setTimeout(() => this.connect(), delay);
  }

  private handleMessage(message: WebSocketMessage): void {
    // Normalize snake_case to camelCase
    const normalizedMessage = {
      ...message,
      cvId: message.cvId || message.cv_id,
      progress: message.progress || message.progress_percent,
    };

    // Skip system messages
    if (['connected', 'subscribed', 'pong'].includes(message.type)) {
      console.log('WebSocket system message:', message.type);
      return;
    }

    const handlers = this.messageHandlers.get(message.type);
    if (handlers) {
      handlers.forEach(handler => handler(normalizedMessage));
    }

    // Also notify handlers subscribed to specific CV
    const cvId = normalizedMessage.cvId;
    if (cvId) {
      const cvHandlers = this.messageHandlers.get(`cv:${cvId}`);
      if (cvHandlers) {
        cvHandlers.forEach(handler => handler(normalizedMessage));
      }
    }

    // Notify all handlers
    const allHandlers = this.messageHandlers.get('*');
    if (allHandlers) {
      allHandlers.forEach(handler => handler(normalizedMessage));
    }
  }

  /**
   * Send a message to the server to subscribe to CV updates
   */
  subscribeToServer(cvId: string, userId: string): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({
        type: 'subscribe',
        cvId,
        userId,
      }));
    }
  }

  /**
   * Send a message to the server to unsubscribe from CV updates
   */
  unsubscribeFromServer(cvId: string): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({
        type: 'unsubscribe',
        cvId,
      }));
    }
  }

  subscribe(event: string, handler: MessageHandler): () => void {
    if (!this.messageHandlers.has(event)) {
      this.messageHandlers.set(event, new Set());
    }
    this.messageHandlers.get(event)!.add(handler);

    return () => {
      this.messageHandlers.get(event)?.delete(handler);
    };
  }

  subscribeToCv(cvId: string, handler: MessageHandler): () => void {
    return this.subscribe(`cv:${cvId}`, handler);
  }

  onConnect(handler: ConnectionHandler): () => void {
    this.onConnectHandlers.add(handler);
    return () => this.onConnectHandlers.delete(handler);
  }

  onDisconnect(handler: ConnectionHandler): () => void {
    this.onDisconnectHandlers.add(handler);
    return () => this.onDisconnectHandlers.delete(handler);
  }

  disconnect(): void {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }
}

// Singleton instance
export const wsClient = new WebSocketClient();

// React hook for WebSocket
import { useEffect, useState, useCallback } from 'react';

export function useWebSocket() {
  const [isConnected, setIsConnected] = useState(wsClient.isConnected());

  useEffect(() => {
    wsClient.connect();

    const unsubConnect = wsClient.onConnect(() => setIsConnected(true));
    const unsubDisconnect = wsClient.onDisconnect(() => setIsConnected(false));

    return () => {
      unsubConnect();
      unsubDisconnect();
    };
  }, []);

  const subscribe = useCallback((event: string, handler: MessageHandler) => {
    return wsClient.subscribe(event, handler);
  }, []);

  const subscribeToCv = useCallback((cvId: string, handler: MessageHandler) => {
    return wsClient.subscribeToCv(cvId, handler);
  }, []);

  const subscribeToServer = useCallback((cvId: string, userId: string) => {
    wsClient.subscribeToServer(cvId, userId);
  }, []);

  const unsubscribeFromServer = useCallback((cvId: string) => {
    wsClient.unsubscribeFromServer(cvId);
  }, []);

  return { isConnected, subscribe, subscribeToCv, subscribeToServer, unsubscribeFromServer };
}

export function useCvProcessingStatus(cvId: string | null, userId?: string) {
  const [status, setStatus] = useState<{
    progress: number;
    status: string;
    error?: string;
  } | null>(null);

  useEffect(() => {
    if (!cvId) return;

    wsClient.connect();

    // Subscribe to the server for this CV
    if (userId) {
      wsClient.subscribeToServer(cvId, userId);
    }

    const unsubscribe = wsClient.subscribeToCv(cvId, (message) => {
      setStatus({
        progress: message.progress || 0,
        status: message.type.replace('processing_', ''),
        error: message.error,
      });
    });

    return () => {
      unsubscribe();
      if (userId) {
        wsClient.unsubscribeFromServer(cvId);
      }
    };
  }, [cvId, userId]);

  return status;
}

/**
 * Hook for subscribing to all processing CVs updates
 * Useful for list views where multiple CVs might be processing
 */
export function useCvListProcessingUpdates(
  cvIds: string[],
  userId: string | undefined,
  onUpdate: (cvId: string, status: string, progress: number, error?: string) => void
) {
  useEffect(() => {
    if (cvIds.length === 0 || !userId) return;

    wsClient.connect();

    // Subscribe to each processing CV on the server
    cvIds.forEach(cvId => {
      wsClient.subscribeToServer(cvId, userId);
    });

    // Listen for all processing events
    const unsubscribe = wsClient.subscribe('*', (message: any) => {
      const cvId = message.cvId || message.cv_id;
      if (cvId && cvIds.includes(cvId)) {
        const status = message.type.replace('processing_', '');
        const progress = message.progress || message.progress_percent || 0;
        onUpdate(cvId, status, progress, message.error);
      }
    });

    return () => {
      unsubscribe();
      cvIds.forEach(cvId => {
        wsClient.unsubscribeFromServer(cvId);
      });
    };
  }, [cvIds.join(','), userId, onUpdate]);
}

export default wsClient;
